import { capitalize, padDateOrTime as pad } from '@pdcfrontendui/utils';
import {
  differenceInCalendarDays,
  subDays,
  isMonday,
  getWeek,
  isSameDay,
  isSameYear,
} from 'date-fns';
import type { PossibleLangCode } from '../language';
import { isStartOfDay } from '../brands';

function isSameDayMidnightInclusive(date: Date, reference: Date) {
  return (
    isSameDay(date, reference) ||
    (isStartOfDay(date) && differenceInCalendarDays(date, reference) === 1)
  );
}

const monthArrayLookup = {
  Da: [
    'januar',
    'februar',
    'marts',
    'april',
    'maj',
    'juni',
    'juli',
    'august',
    'september',
    'oktober',
    'november',
    'december',
  ],
  En: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  Ar: [
    'يناير',
    'فبراير',
    'مارس',
    'أبريل',
    'مايو',
    'يونيو',
    'يوليو',
    'أغسطس',
    'سبتمبر',
    'أكتوبر',
    'نوفمبر',
    'ديسمبر',
  ],
  De: [
    'januar',
    'februar',
    'märz',
    'april',
    'mai',
    'juni',
    'juli',
    'august',
    'september',
    'oktober',
    'november',
    'dezember',
  ],
  Es: [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ],
  Fr: [
    'janvier',
    'février',
    'mars',
    'avril',
    'mai',
    'juin',
    'juillet',
    'août',
    'septembre',
    'octobre',
    'novembre',
    'décembre',
  ],
  Fo: [
    'januar',
    'februar',
    'mars',
    'april',
    'mai',
    'juni',
    'juli',
    'august',
    'september',
    'oktober',
    'november',
    'desember',
  ],
  No: [
    'januar',
    'februar',
    'mars',
    'april',
    'mai',
    'juni',
    'juli',
    'august',
    'september',
    'oktober',
    'november',
    'desember',
  ],
  Pl: [
    'styczeń',
    'luty',
    'marzec',
    'kwiecień',
    'maj',
    'czerwiec',
    'lipiec',
    'sierpień',
    'wrzesień',
    'październik',
    'listopad',
    'grudzień',
  ],
  Ru: [
    'январь',
    'февраль',
    'март',
    'апрель',
    'май',
    'июнь',
    'июль',
    'август',
    'сентябрь',
    'октябрь',
    'ноябрь',
    'декабрь',
  ],
  Se: [
    'januari',
    'februari',
    'mars',
    'april',
    'maj',
    'juni',
    'juli',
    'augusti',
    'september',
    'oktober',
    'november',
    'december',
  ],
} as const satisfies Record<
  PossibleLangCode,
  readonly string[] & { length: 12 }
>;

const weekdayArrayLookup = {
  Da: ['mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag', 'søndag'],
  En: [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ],
  Ar: ['الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد'],
  De: [
    'Montag',
    'Dienstag',
    'Mittwoch',
    'Donnerstag',
    'Freitag',
    'Samstag',
    'Sonntag',
  ],
  Es: [
    'lunes',
    'martes',
    'miércoles',
    'jueves',
    'viernes',
    'sábado',
    'domingo',
  ],
  Fr: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'],
  Fo: [
    'mánadagur',
    'týsdagur',
    'mikudagur',
    'hósdagur',
    'fríggjadagur',
    'leygardagur',
    'sunnudagur',
  ],
  No: ['mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag', 'søndag'],
  Pl: [
    'poniedziałek',
    'wtorek',
    'środa',
    'czwartek',
    'piątek',
    'sobota',
    'niedziela',
  ],
  Ru: [
    'понедельник',
    'вторник',
    'среда',
    'четверг',
    'пятница',
    'суббота',
    'воскресенье',
  ],
  Se: ['måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag', 'söndag'],
} as const satisfies Record<
  PossibleLangCode,
  readonly string[] & { length: 7 }
>;

export class DateFormatter<T extends PossibleLangCode = PossibleLangCode> {
  constructor(public code: T) {}

  private get week() {
    switch (this.code) {
      case 'Da':
        return 'uge';
      case 'En':
        return 'week';
      case 'Fo':
        return 'vika';
      case 'No':
        return 'uke';
      case 'Pl':
        return 'tydzień';
      case 'Ru':
        return 'неделя';
      case 'Se':
        return 'vecka';
      case 'Ar':
        return 'أسبوع';
      case 'De':
        return 'Woche';
      case 'Es':
        return 'semana';
      case 'Fr':
        return 'semaine';
      default:
        return 'week';
    }
  }

  private get hours() {
    switch (this.code) {
      case 'Da':
        return 'timer';
      case 'En':
        return 'hours';
      case 'Fo':
        return 'tímar';
      case 'No':
        return 'timer';
      case 'Pl':
        return 'godziny';
      case 'Ru':
        return 'Часы';
      case 'Se':
        return 'timmar';
      case 'Ar':
        return 'ساعات';
      case 'De':
        return 'Stunden';
      case 'Es':
        return 'horas';
      case 'Fr':
        return 'heures';
      default:
        return 'hours';
    }
  }

  private get hour() {
    switch (this.code) {
      case 'Da':
        return 'time';
      case 'En':
        return 'hour';
      case 'Fo':
        return 'tími';
      case 'No':
        return 'time';
      case 'Pl':
        return 'godzina';
      case 'Ru':
        return 'Час';
      case 'Se':
        return 'timme';
      case 'Ar':
        return 'ساعة';
      case 'De':
        return 'Stunde';
      case 'Es':
        return 'hora';
      case 'Fr':
        return 'heure';
      default:
        return 'hour';
    }
  }

  private get minutes() {
    switch (this.code) {
      case 'Da':
        return 'minutter';
      case 'En':
        return 'minutes';
      case 'Fo':
        return 'minuttir';
      case 'No':
        return 'minutter';
      case 'Pl':
        return 'minuty';
      case 'Ru':
        return 'Минуты';
      case 'Se':
        return 'minuter';
      case 'Ar':
        return 'دقائق';
      case 'De':
        return 'Minuten';
      case 'Es':
        return 'minutos';
      case 'Fr':
        return 'minutes';
      default:
        return 'minutes';
    }
  }

  private get minute() {
    switch (this.code) {
      case 'Da':
        return 'minut';
      case 'En':
        return 'minute';
      case 'Fo':
        return 'minutt';
      case 'No':
        return 'minutt';
      case 'Pl':
        return 'minuta';
      case 'Ru':
        return 'Минута';
      case 'Se':
        return 'minut';
      case 'Ar':
        return 'دقيقة';
      case 'De':
        return 'Minute';
      case 'Es':
        return 'minuto';
      case 'Fr':
        return 'minute';
      default:
        return 'minute';
    }
  }

  private weekNumber(date: Date): number {
    return getWeek(date, {
      firstWeekContainsDate: 4,
      weekStartsOn: 1,
    });
  }

  private get weekdayArray() {
    return weekdayArrayLookup[this.code];
  }

  private get monthArray() {
    return monthArrayLookup[this.code];
  }

  private get dateDot() {
    return this.code === 'Da' || this.code === 'No' || this.code === 'Fo'
      ? '.'
      : '';
  }

  /**
   * Integer between 1 and 31, unpadded.
   */
  public day(date: Date) {
    return date.getDate().toString() as
      | '1'
      | '2'
      | '3'
      | '4'
      | '5'
      | '6'
      | '7'
      | '8'
      | '9'
      | '10'
      | '11'
      | '12'
      | '13'
      | '14'
      | '15'
      | '16'
      | '17'
      | '18'
      | '19'
      | '20'
      | '21'
      | '22'
      | '23'
      | '24'
      | '25'
      | '26'
      | '27'
      | '28'
      | '29'
      | '30'
      | '31';
  }

  /**
   * Integer between 1 and 31, two digits, zero-padded.
   */
  public dayPadded(date: Date) {
    return pad(date.getDate()) as
      | '01'
      | '02'
      | '03'
      | '04'
      | '05'
      | '06'
      | '07'
      | '08'
      | '09'
      | '10'
      | '11'
      | '12'
      | '13'
      | '14'
      | '15'
      | '16'
      | '17'
      | '18'
      | '19'
      | '20'
      | '21'
      | '22'
      | '23'
      | '24'
      | '25'
      | '26'
      | '27'
      | '28'
      | '29'
      | '30'
      | '31';
  }

  /**
   * `Danish`: mandag, tirsdag, onsdag, torsdag, fredag, lørdag, søndag
   * `English`: Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday
   */
  public weekday(date: Date) {
    // Date.prototype.getDay returns 0 for Sunday, 1 for Monday, ..., 6 for Saturday.
    // We want to shift this so that Monday is 0, Tuesday is 1, ..., Sunday is 6.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.weekdayArray[(date.getDay() + 6) % 7]!;
  }
  /**
   * `Danish`: Mandag, Tirsdag, Onsdag, Torsdag, Fredag, Lørdag, Søndag
   * `English`: Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday
   */
  public weekdayCap(date: Date) {
    return capitalize(this.weekday(date));
  }

  /**
   * `Danish`: man, tir, ons, tor, fre, lør, søn
   * `English`: Mon, Tue, Wed, Thu, Fri, Sat, Sun
   */
  public weekdayShort(date: Date) {
    return this.weekday(date).slice(0, 3);
  }
  /**
   * `Danish`: Man, Tir, Ons, Tor, Fre, Lør, Søn
   * `English`: Mon, Tue, Wed, Thu, Fri, Sat, Sun
   */
  public weekdayShortCap(date: Date) {
    return capitalize(this.weekdayShort(date));
  }

  /**
   * `Danish`: jan, feb, mar, apr, maj, jun, jul, aug, sep, okt, nov, dec
   * `English`: Jan, Feb, Mar, Apr, May, Jun, Jul, Aug, Sep, Oct, Nov, Dec
   */
  public monthShort(date: Date) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.monthArray[date.getMonth()]!.slice(0, 3);
  }

  /**
   * `Danish`: Jan, Feb, Mar, Apr, Maj, Jun, Jul, Aug, Sep, Okt, Nov, Dec
   * `English`: Jan, Feb, Mar, Apr, May, Jun, Jul, Aug, Sep, Oct, Nov, Dec
   */
  public monthShortCap(date: Date) {
    return capitalize(this.monthShort(date));
  }

  /**
   * `Danish`: tir 8
   * `English`: Tue 8
   */
  public weekdayDay(date: Date) {
    return `${this.weekdayShort(date)} ${this.day(date)}`;
  }

  /**
   * `Danish`: Tir 8
   * `English`: Tue 8
   */
  public weekdayDayCap(date: Date) {
    return capitalize(this.weekdayDay(date));
  }

  /**
   * `Danish`: tir 8. okt
   * `English`: Tue, 8. Oct
   */
  public weekdayDayMonth(date: Date, isIntervalEnd = false) {
    if (isIntervalEnd && isStartOfDay(date)) {
      date = subDays(date, 1);
    }
    const comma = this.code === 'En' ? ', ' : ' ';
    return `${this.weekdayShort(date)}${comma}${this.day(date)}${this.dateDot} ${this.monthShort(date)}`;
  }

  /**
   * `Danish`: Tir 8. okt
   * `English`: Tue, 8. Oct
   */
  public weekdayDayMonthCap(date: Date, isIntervalEnd = false) {
    return capitalize(this.weekdayDayMonth(date, isIntervalEnd));
  }

  /**
   * `Danish`: 8. okt
   * `English`: 8. Oct
   */
  public dayMonth(date: Date, isIntervalEnd = false) {
    if (isIntervalEnd && isStartOfDay(date)) {
      date = subDays(date, 1);
    }
    return `${this.day(date)}${this.dateDot} ${this.monthShort(date)}`;
  }

  /**
   * @param isIntervalEnd - If true, the date is considered the end of an interval, meaning that 00.00 will be considered 24.00 the day before.
   * `Danish`: 8. okt 2024
   * `English`: 8 Oct 2024
   */
  public dayMonthYear(date: Date, isIntervalEnd = false) {
    if (isIntervalEnd && isStartOfDay(date)) {
      date = subDays(date, 1);
    }
    return `${this.day(date)}${this.dateDot} ${this.monthShort(date)} ${date.getFullYear()}`;
  }

  /**
   * `Danish`: tir 8. okt 2024
   * `English`: Tue, 8 Oct 2024
   */
  public weekdayDayMonthYear(date: Date) {
    const comma = this.code === 'En' ? ', ' : ' ';
    return `${this.weekdayShort(date)}${comma}${this.day(date)}${this.dateDot} ${this.monthShort(date)} ${date.getFullYear()}`;
  }

  /**
   * `Danish`: Tir 8. okt 2024
   * `English`: Tue, 8. Oct 2024
   */
  public weekdayDayMonthYearCap(date: Date) {
    return capitalize(this.weekdayDayMonthYear(date));
  }

  /**
   * `Danish`: okt 2024
   * `English`: Oct 2024
   */
  public monthYear(date: Date) {
    return `${this.monthShort(date)} ${date.getFullYear()}`;
  }

  /**
   * `Danish`: Okt 2024
   * `English`: Oct 2024
   */
  public monthYearCap(date: Date) {
    return capitalize(this.monthYear(date));
  }

  /**
   * `Danish`: 12.30
   * `English`: 12.30
   */
  public time(date: Date, allow24Hours: boolean) {
    return allow24Hours && isStartOfDay(date)
      ? '24.00'
      : `${pad(date.getHours())}.${pad(date.getMinutes())}`;
  }

  /**
   * `Danish`: 12.30 - 13.30 | 12.30 - man 3. marts 2024 13.30 | lør 1. marts 2024 12.30 - 13.30 | lør 1. marts 2024 12.30 - 3. marts 2024 13.30
   * `English`: 12.30 - 13.30 | 12.30 - Mon 3 Mar 2024 13.30 | Sat 1 Mar 2024 12.30 - 13.30 | Sat 1 Mar 2024 12.30 - Mon 3 Mar 2024 13.30
   */
  public timeInterval(
    start: Date,
    end: Date,
    relativeTo?: Date,
    capitalizeWeekday = false
  ) {
    if (relativeTo) {
      const startsOnDay = isSameDay(start, relativeTo);
      const endsOnDay = isSameDayMidnightInclusive(end, relativeTo);
      if (startsOnDay && endsOnDay) {
        return `${this.time(start, false)} - ${this.time(end, true)}`;
      } else if (startsOnDay) {
        const weekdayEnd = this.weekdayDayMonth(end, true);
        return `${this.time(start, false)} - ${capitalizeWeekday ? capitalize(weekdayEnd) : weekdayEnd} ${this.time(end, true)}`;
      } else if (endsOnDay) {
        const weekdayStart = this.weekdayDayMonth(start);
        return `${capitalizeWeekday ? capitalize(weekdayStart) : weekdayStart} ${this.time(start, false)} - ${this.time(end, true)}`;
      }
      const weekdayStart = this.weekdayDayMonth(start);
      const weekdayEnd = this.weekdayDayMonth(end, true);
      return `${capitalizeWeekday ? capitalize(weekdayStart) : weekdayStart} ${this.time(start, false)} - ${capitalizeWeekday ? capitalize(weekdayEnd) : weekdayEnd} ${this.time(end, true)}`;
    }

    const weekdayStart = this.weekdayDayMonth(start);
    const weekdayEnd = this.weekdayDayMonth(end);
    return `${capitalizeWeekday ? capitalize(weekdayStart) : weekdayStart} ${this.time(start, false)} - ${isSameDayMidnightInclusive(start, end) ? this.time(end, true) : `${capitalizeWeekday ? capitalize(weekdayEnd) : weekdayEnd} ${this.time(end, true)}`}`;
  }

  /**
   * `Danish`: tir 8. okt 12.30
   * `English`: Tue, 8. Oct 12.30
   */
  public timeIntervalLongStart(start: Date, relativeTo?: Date) {
    if (relativeTo && isSameDay(start, relativeTo)) {
      return this.time(start, false);
    }
    return `${this.weekdayDayMonth(start)} ${this.time(start, false)}`;
  }

  /**
   * `Danish`: tor 10. okt 13.30
   * `English`: Thu, 10. Oct 13.30
   */
  public timeIntervalLongEnd(end: Date, relativeTo?: Date) {
    if (relativeTo && isSameDayMidnightInclusive(end, relativeTo)) {
      return this.time(end, true);
    }
    return `${this.dayMonth(end)} ${this.time(end, true)}`;
  }

  /**
   * `Danish`: tir 8. okt 12.30 - tor 10. okt 13.30
   * `English`: Tue, 8. Oct 12.30 - Thu, 10. Oct 13.30
   */
  public timeIntervalLong(start: Date, end: Date, relativeTo?: Date) {
    return `${this.timeIntervalLongStart(start, relativeTo)} - ${this.timeIntervalLongEnd(end, relativeTo ?? start)}`;
  }

  /**
   * `Danish`: Tir 8. okt 12.30
   * `English`: Tue, 8. Oct 12.30
   */
  public timeIntervalLongStartCap(start: Date, relativeTo?: Date) {
    return capitalize(this.timeIntervalLongStart(start, relativeTo));
  }

  /**
   * `Danish`: Tor 10. okt 13.30
   * `English`: Thu, 10. Oct 13.30
   */
  public timeIntervalLongEndCap(end: Date, relativeTo?: Date) {
    return capitalize(this.timeIntervalLongEnd(end, relativeTo));
  }

  /**
   * `Danish`: Tir 8. okt 12.30 - tor 10. okt 13.30
   * `English`: Tue, 8. Oct 12.30 - Thu, 10. Oct 13.30
   */
  public timeIntervalLongCap(start: Date, end: Date, relativeTo?: Date) {
    return capitalize(this.timeIntervalLong(start, end, relativeTo));
  }

  /**
   * `Danish`: 8. okt 12.30
   * `English`: 8. Oct 12.30
   */
  public dayMonthShortTime(date: Date, allow24Hours: boolean) {
    return `${this.dayMonth(date)} ${this.time(date, allow24Hours)}`;
  }

  /**
   * `Danish`: uge 35, okt 2024
   * `English`: week 35, Oct 2024
   */
  public weekDescription(date: Date) {
    return `${this.week} ${this.weekNumber(date)}, ${this.monthShort(date)} ${date.getFullYear()}`;
  }

  /**
   * `Danish`: Uge 35, okt 2024
   * `English`: Week 35, Oct 2024
   */
  public weekDescriptionCap(date: Date) {
    return capitalize(this.weekDescription(date));
  }

  public durationHoursMinutes(durationMs: number): string;
  public durationHoursMinutes(start: Date, end: Date): string;
  /**
   * Format a duration in hours and minutes. Shouldn't be used for durations longer than 24 hours.
   * `Danish`: 8 timer, 30 minutter
   * `English`: 8 hours, 30 minutes
   */
  public durationHoursMinutes(start: Date | number, end?: Date) {
    const diff =
      (typeof start === 'number' && !end
        ? start
        : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          Math.abs(end!.getTime() - (start as Date).getTime())) / 1000; // seconds
    const hours = Math.floor(diff / 3600); // hours
    const minutes = Math.floor((diff % 3600) / 60); // minutes
    return `${hours} ${hours === 1 ? this.hour : this.hours}, ${minutes} ${
      minutes === 1 ? this.minute : this.minutes
    }`;
  }

  /**
   * Active timer.
   * 01.02.03
   */
  public durationTimer(from: Date, to: Date) {
    const diffMs = to.getTime() - from.getTime();
    const hours = pad(Math.floor(diffMs / 3600000));
    const minutes = pad(Math.floor((diffMs % 3600000) / 60000));
    const seconds = pad(Math.floor((diffMs % 60000) / 1000));
    return `${hours}.${minutes}.${seconds}`;
  }

  /**
   * `Danish`: uge 35 - 37
   * `English`: week 35 - 37
   */
  public intervalWeek(start: Date, end: Date) {
    return `${this.week} ${this.weekNumber(start)} - ${isMonday(end) && isStartOfDay(end) ? this.weekNumber(subDays(end, 1)) : this.weekNumber(end)}` as const;
  }

  /**
   * `Danish`: Uge 35 - 37
   * `English`: Week 35 - 37
   */
  public intervalWeekCap(start: Date, end: Date) {
    return capitalize(this.intervalWeek(start, end));
  }

  /**
   * If the start and end date are in the same month, it will return the month name only once.
   * `Danish`: 8. - 14. jan 2024 | 8. jan - 14.marts 2024 | 8. jan 2024 - 14. marts 2025
   * `English`: 8 - 14 Jan 2024 | 8 Jan - 14 Mar 2024 | 8 Jan 2024 - 14 Mar 2025
   */
  public intervalDay(start: Date, end: Date) {
    if (start < end && isStartOfDay(end)) {
      end = subDays(end, 1);
    }
    const startDay = start.getDate();
    const endDay = end.getDate();
    const startMonth = this.monthShort(start);
    const startYear = start.getFullYear();
    const endMonth = this.monthShort(end);
    const endYear = end.getFullYear();
    const dot =
      this.code === 'Da' ||
      this.code === 'Se' ||
      this.code === 'No' ||
      this.code === 'Fo'
        ? '.'
        : '';

    if (startYear === endYear) {
      if (startMonth === endMonth) {
        if (startDay === endDay) {
          return `${startDay}${dot} ${startMonth} ${startYear}`;
        }
        return `${startDay}${dot} - ${endDay}${dot} ${startMonth} ${startYear}`;
      } else {
        return `${startDay}${dot} ${startMonth} - ${endDay}${dot} ${endMonth} ${startYear}`;
      }
    } else {
      return `${startDay}${dot} ${startMonth} ${startYear} - ${endDay}${dot} ${endMonth} ${endYear}`;
    }
  }

  /**
   * `Danish`: torsdag den 16. februar
   * `English`: Thursday, February 16th
   */
  public dayDescription(date: Date, allow24Hours: boolean) {
    if (allow24Hours && isStartOfDay(date)) {
      date = subDays(date, 1);
    }
    const weekday = this.weekday(date);
    const day = date.getDate();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const month = this.monthArray[date.getMonth()]!;
    switch (this.code) {
      case 'Da':
        return `${weekday} den ${day}. ${month}`;
      case 'En':
        return `${weekday}, the ${day}${day % 10 === 1 ? 'st' : day % 10 === 2 ? 'nd' : day % 10 === 3 ? 'rd' : 'th'} of ${month}`;
      case 'Fo':
        return `${weekday} ${day}. ${month}`;
      case 'No':
        return `${weekday} ${day}. ${month}`;
      case 'Se':
        return `${weekday} ${day} ${month}`;
      case 'Es':
        return `${weekday} ${day} de ${month}`;
      case 'Fr':
        return `${weekday} ${day} ${month}`;
      default:
        return `${weekday} ${day} ${month}`;
    }
  }

  /**
   * `Danish`: Torsdag den 16. februar
   * `English`: Thursday, February 16th
   */
  public dayDescriptionCap(date: Date, allow24Hours: boolean) {
    return capitalize(this.dayDescription(date, allow24Hours));
  }

  /**
   * `Danish`: torsdag den 16. februar 08.30 - 09.30 | torsdag den 16. februar 08.30 - fredag den 17. februar 09.30
   * `English`: Thursday, February 16th 08.30 - 09.30 | Thursday, February 16th 08.30 - Friday, February 17th 09.30
   */
  public intervalDayDescription(start: Date, end: Date) {
    return isSameDayMidnightInclusive(start, end)
      ? `${this.dayDescription(start, false)} ${this.time(start, false)} - ${this.time(end, true)}`
      : `${this.dayDescription(start, false)} ${this.time(start, false)} - ${this.dayDescription(end, true)} ${this.time(end, true)}`;
  }

  public intervalDayDescriptionCap(start: Date, end: Date) {
    return capitalize(this.intervalDayDescription(start, end));
  }

  /**
   * `Danish`: 22 / 02 / 1990
   * `English`: 22 / 02 / 1990
   * `Swedish`: 1990 / 02 / 22
   */
  public dateNumeric(date: Date, isIntervalEnd = false) {
    if (isIntervalEnd && isStartOfDay(date)) {
      date = subDays(date, 1);
    }
    switch (this.code) {
      case 'Se':
        return `${date.getFullYear()} / ${pad(date.getMonth() + 1)} / ${pad(date.getDate())}`;
      default:
        return `${pad(date.getDate())} / ${pad(date.getMonth() + 1)} / ${date.getFullYear()}`;
    }
  }

  public shortInexact(date: Date, relativeTo: Date) {
    if (isSameDay(date, relativeTo)) {
      return this.time(date, false);
    }
    if (isSameYear(date, relativeTo)) {
      return this.dayMonthShortTime(date, false);
    }
    return `${this.dayMonthYear(date)} ${this.time(date, false)}`;
  }
}
